import { FC, memo } from 'react';

import SectionLeadLeftAlign from '@components_iqfu/public/section_lead_left_align/SectionLeadLeftAlign';

import { SalesStatsData } from '@type-def_iqfu/public/MarketData';

import MansionIcon from '/public/images/iqfu/public/trend_description/mansion_icon.svg';
import LandIcon from '/public/images/iqfu/public/trend_description/land_icon.svg';
import HouseIcon from '/public/images/iqfu/public/trend_description/house_icon.svg';

import { PublicPropertyType } from '@constants_iqfu';

import styles from './TrendDescription.module.scss';

type Props = {
  displayData: SalesStatsData;
  propertyType: keyof typeof PublicPropertyType;
  areaInfo: { prefecture?: string; city?: string; parent_major_city?: string; city_category?: string };
};

const TrendDescription: FC<Props> = memo(({ propertyType, displayData, areaInfo }) => {
  //投入されるデータ仕様：urban_area、suburban_area、local_areaのarea_typeは一致、かつtrend_descriptionとdemand_descriptionのarea_typeも一致
  const areaType = displayData.sales_markets.urban_area.trend_description?.area_type;

  const displayAreaName = () => {
    switch (areaType) {
      case 'country':
        return '全国';
      case 'prefecture':
        return areaInfo.prefecture ?? '';
      case 'city':
        return areaInfo.city ?? '';
      case 'major_city':
        return areaInfo.city_category === 'major' ? areaInfo.city ?? '' : areaInfo.parent_major_city ?? '';
      default:
        return '';
    }
  };

  return (
    <>
      {(displayData.sales_markets.urban_area.trend_description?.value ||
        displayData.sales_markets.suburban_area.trend_description?.value ||
        displayData.sales_markets.local_area.trend_description?.value) && (
        <div className={styles.section_lead_left_container}>
          <SectionLeadLeftAlign>
            {displayAreaName()}の{PublicPropertyType[propertyType]}売却動向を解説
          </SectionLeadLeftAlign>
        </div>
      )}
      {/* TODO 改善チームでデータができ次第、市区町村・町域ページの場合は、該当のcityが都市部or 郊外or地方で表示を絞り込む */}

      <div className={styles.description_container}>
        {displayData.sales_markets.urban_area.trend_description?.value && (
          <div className={styles.contents_block}>
            <div className={styles.summary}>
              <div className={styles.block_container}>
                <div className={styles.block_title}>
                  今高く売れる
                  <span className={styles.large_text}>
                    「{areaType === 'country' ? '' : `${displayAreaName()} `}都市部」
                  </span>
                  の{areaType === 'country' && <br />}
                  {PublicPropertyType[propertyType]}の傾向は・・・
                </div>
                {propertyType === 'mansion' && <MansionIcon className={styles.property_icon_mansion} />}
                {propertyType === 'land' && <LandIcon className={styles.property_icon} />}
                {propertyType === 'house' && <HouseIcon className={styles.property_icon} />}
              </div>
              {displayData.sales_markets.urban_area.sample_cities && (
                <div className={styles.sample_cities}>{displayData.sales_markets.urban_area.sample_cities}</div>
              )}
              <div className={styles.summary_value}>
                {displayData.sales_markets.urban_area.demand_description.value}
              </div>
            </div>
            <div className={styles.bottom_contents}>
              <div>
                <div className={styles.block_title}>
                  {areaType === 'country' ? '' : `${displayAreaName()} `}都市部の値動き
                </div>
                <div className={styles.contents_text}>
                  {displayData.sales_markets.urban_area.trend_description?.value}
                </div>
              </div>
            </div>
          </div>
        )}

        {displayData.sales_markets.suburban_area.trend_description?.value && (
          <div className={styles.contents_block}>
            <div className={styles.summary}>
              <div className={styles.block_container}>
                <div className={styles.block_title}>
                  今高く売れる
                  <span className={styles.large_text}>
                    「{areaType === 'country' ? '' : `${displayAreaName()} `}郊外部」
                  </span>
                  の{areaType === 'country' && <br />}
                  {PublicPropertyType[propertyType]}の傾向は・・・
                </div>
                {propertyType === 'mansion' && <MansionIcon className={styles.property_icon_mansion} />}
                {propertyType === 'land' && <LandIcon className={styles.property_icon} />}
                {propertyType === 'house' && <HouseIcon className={styles.property_icon} />}
              </div>
              {displayData.sales_markets.suburban_area.sample_cities && (
                <div className={styles.sample_cities}>{displayData.sales_markets.suburban_area.sample_cities}</div>
              )}
              <div className={styles.summary_value}>
                {displayData.sales_markets.suburban_area.demand_description.value}
              </div>
            </div>
            <div className={styles.bottom_contents}>
              <div>
                <div className={styles.block_title}>
                  {areaType === 'country' ? '' : `${displayAreaName()} `}郊外部の値動き
                </div>
                <div className={styles.contents_text}>
                  {displayData.sales_markets.suburban_area.trend_description?.value}
                </div>
              </div>
            </div>
          </div>
        )}

        {displayData.sales_markets.local_area.trend_description?.value && (
          <div className={styles.contents_block}>
            <div className={styles.summary}>
              <div className={styles.block_container}>
                <div className={styles.block_title}>
                  今高く売れる
                  <span className={styles.large_text}>
                    「{areaType === 'country' ? '' : `${displayAreaName()} `}地方部」
                  </span>
                  の{areaType === 'country' && <br />}
                  {PublicPropertyType[propertyType]}の傾向は・・・
                </div>
                {propertyType === 'mansion' && <MansionIcon className={styles.property_icon_mansion} />}
                {propertyType === 'land' && <LandIcon className={styles.property_icon} />}
                {propertyType === 'house' && <HouseIcon className={styles.property_icon} />}
              </div>
              {displayData.sales_markets.local_area.sample_cities && (
                <div className={styles.sample_cities}>{displayData.sales_markets.local_area.sample_cities}</div>
              )}
              <div className={styles.summary_value}>
                {displayData.sales_markets.local_area.demand_description.value}
              </div>
            </div>
            <div className={styles.bottom_contents}>
              <div>
                <div className={styles.block_title}>
                  {areaType === 'country' ? '' : `${displayAreaName()} `}地方部の値動き
                </div>
                <div className={styles.contents_text}>
                  {displayData.sales_markets.local_area.trend_description?.value}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
});

TrendDescription.displayName = 'TrendDescription';

export default TrendDescription;
